import { ComponentProps } from 'react';

import useTrans from '../../hooks/ui/useTrans';
import AppliedCouponCode from '../../types/AppliedCouponCode';
import MoneyValue from '../MoneyValue/MoneyValue';
import Text from '../Text/Text';

const unitMap = {
    m: 'minutes',
    h: 'hours',
    d: 'days',
};

const variablesForCoupon = (
    coupon: AppliedCouponCode,
    currencyCode: string
) => {
    switch (coupon.type) {
        case 'Percentage':
            return { percentage: coupon.percentage };

        case 'FixedAmount':
            return {
                amount: (
                    <MoneyValue
                        value={coupon.amount}
                        currencyCode={currencyCode}
                    />
                ),
            };

        case 'FreeTime':
        case 'Giveaway':
        default:
            return {};
    }
};

type CouponTypeLabelProps = {
    coupon: AppliedCouponCode;
    currencyCode: string;
} & Omit<ComponentProps<typeof Text>, 'children'>;

const CouponTypeLabel = ({
    coupon,
    currencyCode,
    ...textProps
}: CouponTypeLabelProps) => {
    const transShared = useTrans('General.Shared.');
    const transUnits = useTrans('General.Units.');

    /**
     * Free time works differently because of the units
     */
    if (coupon.type === 'FreeTime') {
        const time = coupon.freeTime;
        const fullUnitFromAbbreviation =
            unitMap[time.charAt(time.length - 1) as keyof typeof unitMap];

        return (
            <Text {...textProps}>
                {transShared(`CouponTypeLabel.${coupon.type}`, {
                    duration: transUnits(`Duration`, {
                        duration: time.substring(0, time.length - 1),
                        unit: fullUnitFromAbbreviation,
                    }),
                })}
            </Text>
        );
    }

    return (
        <Text {...textProps}>
            {transShared(
                `CouponTypeLabel.${coupon.type}`,
                variablesForCoupon(coupon, currencyCode)
            )}
        </Text>
    );
};

export default CouponTypeLabel;
